import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import { blue, green } from '@mui/material/colors';
import {
  DayOfWeek,
  LocationHours,
  OrderType,
} from 'api/ordering-service/ordering-service.types';
import i18n from 'app-i18n';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import {
  DUMMY_EVENT_ID,
  SetHoursFormDataProps,
  dayOfWeekMap,
} from '../location-hours.utils';

export type ExtendedPropsType = {
  orderType: OrderType;
  eventType: 'LocationHours';
};

export const convertNormalHoursToEventInput = (
  hours: LocationHours[],
  extendedProps: ExtendedPropsType,
): EventInput[] =>
  hours?.reduce(
    (acc: EventInput[], curr) =>
      acc.concat(
        curr.timeSlots.map(timeSlot => ({
          id: uuidv4(),
          title:
            extendedProps.orderType === 'PICKUP'
              ? i18n.t('LocationHours.eventTitles.pickup')
              : i18n.t('LocationHours.eventTitles.delivery'),
          daysOfWeek: [dayOfWeekMap[curr.dayOfWeek]],
          startTime: timeSlot.startTime,
          endTime: timeSlot.endTime,
          extendedProps,
          color: extendedProps.orderType === 'PICKUP' ? blue[500] : green[500],
        })),
      ),
    [],
  ) ?? [];

export const convertEventApiToRecurring = (event: EventApi): EventInput => {
  const startDateTime = DateTime.fromJSDate(event.start);
  const endDateTime = DateTime.fromJSDate(event.end);

  return {
    id: event.id,
    title: event.title,
    daysOfWeek: [(startDateTime.weekday % 7).toString()],
    startTime: startDateTime.toFormat('HH:mm:ss'),
    endTime: endDateTime.toFormat('HH:mm:ss'),
    extendedProps: event.extendedProps,
    color: event.backgroundColor,
  };
};

export const recurringEventsToNormalHours = (
  acc: LocationHours[],
  curr: EventInput & { startTime: string; endTime: string },
) => {
  const dayOfWeekString =
    // @ts-ignore
    curr.daysOfWeek[0];
  const dayOfWeek = Object.keys(dayOfWeekMap).find(
    key => dayOfWeekMap[key] === dayOfWeekString,
  ) as DayOfWeek;

  const existingDay = acc.find(day => day.dayOfWeek === dayOfWeek);
  if (existingDay) {
    existingDay.timeSlots.push({
      startTime: curr.startTime,
      endTime: curr.endTime,
    });
  } else {
    acc.push({
      dayOfWeek,
      // TODO Need the BE to confirm here that startTime and endTime are not needed anymore
      // They can only record one time slot and it is redundant with the timeSlots array
      startTime: '',
      endTime: '',
      timeSlots: [
        {
          startTime: curr.startTime,
          endTime: curr.endTime,
        },
      ],
    });
  }

  return acc;
};

export const convertSetNormalHoursFormDataToEventSource = (
  data: SetHoursFormDataProps,
): EventInput[] => {
  const { daysOfWeek, timeRange, orderType } = data;
  const startTime = DateTime.fromISO(timeRange[0]).toFormat('HH:mm:ss');
  const endTime = DateTime.fromISO(timeRange[1]).toFormat('HH:mm:ss');

  return daysOfWeek.flatMap(day => {
    return orderType.map(type => {
      return {
        id: uuidv4(),
        title:
          type === 'PICKUP'
            ? i18n.t('LocationHours.eventTitles.pickup')
            : i18n.t('LocationHours.eventTitles.delivery'),
        daysOfWeek: [dayOfWeekMap[day.label.toUpperCase()]],
        startTime,
        endTime,
        extendedProps: {
          orderType: type,
          eventType: 'LocationHours',
        },
        color: type === 'PICKUP' ? blue[500] : green[500],
      };
    });
  });
};

export const filterNormalHoursOverlappedEvents = (
  data: SetHoursFormDataProps,
  existingEvents: EventInput[],
): EventInput[] => {
  const { eventId, daysOfWeek, timeRange, orderType } = data;

  if (!timeRange.every(time => DateTime.fromISO(time).isValid)) {
    return [];
  }

  const [startTime, endTime] = timeRange.map(time => {
    const { hour, minute, second, millisecond } = DateTime.fromISO(time);
    return DateTime.fromObject({ hour, minute, second, millisecond });
  });

  const inputInterval = Interval.fromDateTimes(startTime, endTime);

  // Filter events from eventArray based on matching day of week and order type
  return existingEvents.filter(event => {
    const eventStartTime = DateTime.fromFormat(event.startTime, 'HH:mm:ss');
    const eventEndTime = DateTime.fromFormat(event.endTime, 'HH:mm:ss');

    return (
      ![eventId, DUMMY_EVENT_ID].includes(event.id) &&
      orderType.includes(event.extendedProps.orderType) &&
      (daysOfWeek[0]?.id === 'ALL' ||
        daysOfWeek.some(
          day =>
            day.label.toUpperCase() ===
            Object.keys(dayOfWeekMap)[event.daysOfWeek[0]],
        )) &&
      DateTime.fromISO(event.startTime) <= DateTime.fromISO(event.endTime) &&
      Interval.fromDateTimes(eventStartTime, eventEndTime).overlaps(
        inputInterval,
      )
    );
  });
};
